import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useCreateTimeExtensionRequestMutation } from "../api/jupyterManagementApi";

export function RequestExtensionDialog({
  open,
  onClose,
  sessionId,
  onRequested, // optional: notify parent to flip workspace.extension_requested
  isDarkMode = false,
}: {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  onRequested?: () => void;
  isDarkMode?: boolean;
}) {
  const [hh, setHh] = useState("000");
  const [mm, setMm] = useState("15");
  const [reason, setReason] = useState("");
  const mutation = useCreateTimeExtensionRequestMutation();

  const hours = useMemo(
    () => Array.from({ length: 1000 }, (_, i) => String(i).padStart(3, "0")),
    []
  );

  // REPLACE minutes with 0..59:
  const minutes = useMemo(
    () => Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")),
    []
  );

  const totalMinutes = useMemo(
    () => parseInt(hh, 10) * 60 + parseInt(mm, 10),
    [hh, mm]
  );

  const submitting = mutation.isPending;
  const canSubmit =
    !submitting && reason.trim().length >= 10 && totalMinutes > 0;

  const handleRequest = async () => {
    if (totalMinutes < 1) return;
    const duration_value = `${hh}:${mm}`;
    try {
      await mutation.mutateAsync({
        sandbox_session_id: sessionId,
        reason: reason.trim(),
        duration_value,
        duration_unit: "hours",
      });
      onRequested?.(); // parent can set workspace.extension_requested = true
      onClose();
    } catch (e) {
      // TODO: toast error
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !submitting && (v ? undefined : onClose())}
    >
      <DialogContent className="sm:max-w-md dark:bg-[#161b22] dark:border-[#30363d] dark:text-[#f0f6fc]">
        <DialogHeader>
          <DialogTitle className="dark:text-[#f0f6fc]">Request time extension</DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <Label className="text-sm text-gray-700 dark:text-[#8b949e]">Duration (HH:MM)</Label>
          <div className="flex items-center gap-2">
            <select
              className="border rounded px-2 py-1 bg-white dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#f0f6fc]"
              value={hh}
              onChange={(e) => setHh(e.target.value)}
              disabled={submitting}
            >
              {hours.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <span className="text-gray-600 dark:text-gray-400">:</span>
            <select
              className="border rounded px-2 py-1 bg-white dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#f0f6fc]"
              value={mm}
              onChange={(e) => setMm(e.target.value)}
              disabled={submitting}
            >
              {minutes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <Label className="text-sm text-gray-700 dark:text-[#8b949e]">Reason</Label>
          <textarea
            className="w-full border rounded px-2 py-1 min-h-24 bg-white dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#f0f6fc] focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-[#f0883e]"
            placeholder="Provide a clear reason (min 10 chars)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
          />

          <p className="text-xs text-gray-500 dark:text-[#8b949e]">
            Will request {hh}:{mm} hours
          </p>
        </div>

        <DialogFooter className="mt-4">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={submitting}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            Cancel
          </Button>
          <Button
            variant="bits"
            className="bg-bits-blue dark:bg-[#f0883e] dark:hover:bg-[#e0772d] dark:text-white"
            onClick={handleRequest}
            disabled={!canSubmit}
          >
            {submitting ? "Requesting..." : "Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
